import { Link } from "react-router-dom";
import {
  ArrowRight,
  Boxes,
  Bot,
  Gauge,
  Sparkles,
  Workflow,
  Network,
} from "lucide-react";
import { motion } from "framer-motion";

const services = [
  {
    icon: Boxes,
    title: "Strategic Product Engineering",
    description: "Product, platform, and architecture decisions made with founder intent and scale realities in mind.",
    href: "/services",
    glow: "from-[#C9A55C]/30 via-[#C9A55C]/10 to-transparent",
  },
  {
    icon: Bot,
    title: "AI Workflow Systems",
    description: "Agentic workflows, LLM orchestration, and AI-augmented operations built for measurable leverage.",
    href: "/services/ai-enablement",
    glow: "from-[#7c5cff]/30 via-[#5a40d8]/10 to-transparent",
  },
  {
    icon: Gauge,
    title: "Operational Intelligence Platforms",
    description: "Founder dashboards, decision systems, and visibility layers that make the business legible in real time.",
    href: "/services",
    glow: "from-[#2DD4BF]/25 via-[#1E1454]/10 to-transparent",
  },
  {
    icon: Sparkles,
    title: "Founder Copilot Systems",
    description: "AI-enabled operating layers that compress research, reporting, and decision support for leadership.",
    href: "/services",
    glow: "from-[#F59E0B]/30 via-[#C9A55C]/10 to-transparent",
  },
  {
    icon: Workflow,
    title: "Automation &amp; Agentic Workflows",
    description: "n8n, MCP, and orchestration architectures that remove manual operational dependency.",
    href: "/services",
    glow: "from-[#EC4899]/25 via-[#7c5cff]/10 to-transparent",
  },
  {
    icon: Network,
    title: "Platform Architecture &amp; Scalability",
    description: "Modular architecture, multi-tenant systems, and infrastructure planning sized for the next growth stage.",
    href: "/services",
    glow: "from-[#22d3ee]/25 via-[#1E1454]/10 to-transparent",
  },
];

export function ServicesSection() {
  return (
    <section className="relative isolate py-24 md:py-32 overflow-hidden bg-[hsl(248_55%_8%)] text-white">
      {/* dark mesh */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 15% 10%, hsl(40 52% 58% / 0.18), transparent 60%), radial-gradient(50% 50% at 85% 90%, hsl(248 80% 40% / 0.35), transparent 60%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(0 0% 100% / 1) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 1) 1px, transparent 1px)",
          backgroundSize: "120px 120px",
        }}
      />

      <div className="container mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-4">
            Strategic capabilities
          </p>
          <h2 className="font-display font-bold tracking-[-0.02em] leading-[1.1] text-3xl md:text-4xl lg:text-5xl text-white">
            How we apply senior technology and AI judgment.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              viewport={{ once: true, margin: "-80px" }}
            >
              <Link
                to={service.href}
                className="group relative block rounded-2xl p-7 md:p-8 h-full overflow-hidden border border-white/10 bg-white/[0.04] backdrop-blur-md transition-all duration-500 hover:-translate-y-1 hover:border-white/25"
              >
                {/* color glow on hover */}
                <div
                  aria-hidden="true"
                  className={`absolute -inset-1 -z-10 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500 bg-gradient-to-br ${service.glow}`}
                />
                {/* highlight ring */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ boxShadow: "inset 0 1px 0 hsl(0 0% 100% / 0.18)" }}
                />

                <div className="relative">
                  <div className="h-12 w-12 rounded-xl bg-white/10 border border-white/15 grid place-items-center mb-6 group-hover:bg-accent/20 group-hover:border-accent/40 transition-colors">
                    <service.icon className="h-5 w-5 text-accent" />
                  </div>

                  <h3 className="text-xl font-display font-semibold text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-white/65 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <span className="inline-flex items-center gap-2 text-sm font-medium text-accent group-hover:gap-3 transition-all">
                    View Details
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
